import {Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {PaymentService} from "../../api";

@Component({
  selector: 'ui-payment-table',
  templateUrl: './payment-table.component.html',
  styleUrl: './payment-table.component.scss'
})
export class PaymentTableComponent implements OnInit {

  @Output() planSelect = new EventEmitter<Plan>();
  @Input() currentPlanLookupKey?: string;
  billingInterval: 'monthly' | 'yearly' = 'monthly';
  plans: Plan[] = [];
  allPlans: Plan[] = [];

  constructor(private paymentService: PaymentService) {
  }

  ngOnInit() {
    this.paymentService.getProductsEndpoint().subscribe((res) => {
      res.forEach(productWithPrices => {
        const product = productWithPrices.product;
        const prices = productWithPrices.prices || [];
        
        prices.forEach(price => {
          const lookupKey = price.lookup_key || "";
          
          if (!lookupKey) return;
          
          const features = product?.marketing_features
            ?.map(f => f.name)
            .filter((name): name is string => name != null && name !== undefined) || [];
          const interval = price.recurring?.interval || 'month';
          
          const monthlyPrice = interval === 'year' 
            ? (price.unit_amount || 0) / 12 
            : (price.unit_amount || 0);
          
          // Alternativer Preis (monthly vs yearly)
          const alternativePrice = prices.find(p => 
            p.lookup_key && 
            p.lookup_key !== lookupKey && 
            this.getBasePlanKey(p.lookup_key) === this.getBasePlanKey(lookupKey)
          );

          let alternativePriceFormatted: string | undefined;
          let alternativeInterval: string | undefined;
          if (alternativePrice) {
            const altInterval = alternativePrice.recurring?.interval || 'month';
            const altMonthlyAmount = altInterval === 'year'
              ? (alternativePrice.unit_amount || 0) / 12
              : (alternativePrice.unit_amount || 0);
            
            alternativePriceFormatted = `${(altMonthlyAmount / 100).toFixed(2)}€ / Benutzer pro Monat`;
            alternativeInterval = altInterval;
          }
          
          this.allPlans.push({
            name: product?.name || "",
            description: product?.description || "",
            priceLabel: this.formatPrice(price.unit_amount || 0, interval),
            alternativePrice: alternativePriceFormatted,
            alternativeInterval: alternativeInterval,
            features: features,
            priceId: lookupKey,
            popular: lookupKey === "BASIC" || lookupKey === "BASIC_YEARLY",
            billingInterval: interval,
            monthlyEquivalent: monthlyPrice / 100
          });
        });
      });
      
      this.allPlans.sort((a, b) => {
        const orderDiff = PLAN_ORDER[this.getBasePlanKey(a.priceId)] - PLAN_ORDER[this.getBasePlanKey(b.priceId)];
        if (orderDiff !== 0) return orderDiff;
        return a.billingInterval === 'month' ? -1 : 1;
      });
      
      this.filterPlans();
    });
  }

  private formatPrice(unitAmount: number, interval: string): string {
    const amount = unitAmount / 100;
    if (interval === 'year') {
      return `${amount.toFixed(2)}€ / Benutzer pro Jahr`;
    }
    return `${amount.toFixed(2)}€ / Benutzer pro Monat`;
  }

  private getBasePlanKey(lookupKey: string): string {
    return lookupKey.replace('_YEARLY', '');
  }

  filterPlans() {
    this.plans = this.allPlans.filter(plan => {
      if (plan.priceId === 'FREE') return true;
      
      const isYearly = plan.priceId.includes('_YEARLY');
      return this.billingInterval === 'yearly' ? isYearly : !isYearly;
    });
  }

  toggleBillingInterval() {
    this.billingInterval = this.billingInterval === 'monthly' ? 'yearly' : 'monthly';
    this.filterPlans();
  }

  onSelectPlan(plan: Plan) {
    if (this.isCurrentPlan(plan)) {
      return;
    }
    this.planSelect.emit(plan)
  }

  isCurrentPlan(plan: Plan): boolean {
    return this.currentPlanLookupKey === plan.priceId;
  }
}

export interface Plan {
  name: string;
  priceLabel: string;
  alternativePrice?: string;
  alternativeInterval?: string;
  description: string;
  priceId: string;
  popular?: boolean;
  features: string[];
  billingInterval?: string;
  monthlyEquivalent?: number;
}

const PLAN_ORDER: { [key: string]: number } = {
  'FREE': 1,
  'BASIC': 2,
  'PRO': 3,
  'ENTERPRISE': 4,
};

